import algoliasearch from 'algoliasearch';
import aa from "search-insights";
import shopify from "../../shopify";
import cloneDeep from 'lodash/cloneDeep';
import { initial } from 'lodash';

// Make aa globally available
window.aa = aa;

const DOMAIN = 'mj-bale-int-dev.myshopify.com';
const algoliaFacetOrder = null;

// Global settings
const configuredFacets = JSON.parse(algoliaFacetOrder);
const enabledFacets = configuredFacets ? configuredFacets.filter(facet => facet.enabled).map(facet => facet.name) : [];
let usedFacets = [];
let indexes = [];
const sortedFacets = enabledFacets.filter(facet => usedFacets.includes(facet));

const defaultSearchParams = {
    hitsPerPage: 0,
    distinct: 1,
    facetingAfterDistinct: true,
    attributesToHighlight: [],
    attributesToRetrieve: ['-*', 'objectID', 'id', 'query'],
    facets: sortedFacets,
    clickAnalytics: true,
    maxValuesPerFacet: 1000, // Increase limit to show more facet values (default is 100)
};

// Default search parameters for different content types
const searchParamsByType = {
    products: {
        ...defaultSearchParams,
        hitsPerPage: 20,
        attributesToRetrieve: ['objectID', 'id', 'title', 'handle', 'vendor', 'product_type', 'tags', 'variants', 'images', 'price', 'price_min', 'price_max', 'available'],
        facets: ['vendor', 'product_type', 'tags', 'price_range', 'inventory_available']
    },
    articles: {
        ...defaultSearchParams,
        hitsPerPage: 5,
        attributesToRetrieve: ['objectID', 'id', 'title', 'handle', 'content', 'summary', 'author', 'tags', 'created_at', 'image'],
        facets: ['tags', 'author']
    },
    categories: {
        ...defaultSearchParams,
        hitsPerPage: 10,
        attributesToRetrieve: ['objectID', 'id', 'title', 'handle', 'description'],
        facets: []
    },
    suggestions: {
        ...defaultSearchParams,
        hitsPerPage: 8,
        attributesToRetrieve: ['objectID', 'id', 'title', 'query'],
        facets: []
    }
};

export const algolia = {
    name: 'algolia',
    component() {
        return {
            open: false,
            config: {},
            client: null,
            isProcessingRequest: false,
            hasInit: false,
            requestType: '',
            currentRequest: null,
            headerResultsTotal: null,
            productsPerPageSetting: null,
            defaultSort: null,
            requestCountPromise: null,
            insightIndex: '',
            requestCountResolve: null,
            isFiltering: false,
            currentQueryID: null,
            initialFilters: [],

            async init(settings, productsPerPage, headerResultsTotal) {
                if (this.hasInit) return;

                // Restore queryID from sessionStorage if available and recent (within 30 minutes)
                try {
                    const storedQueryID = sessionStorage.getItem('algolia_queryID');
                    const storedTimestamp = sessionStorage.getItem('algolia_queryID_timestamp');
                    const thirtyMinutes = 30 * 60 * 1000;
                    
                    if (storedQueryID && storedTimestamp) {
                        const age = Date.now() - parseInt(storedTimestamp);
                        if (age < thirtyMinutes) {
                            this.currentQueryID = storedQueryID;
                            window.algoliaQueryID = storedQueryID;
                            console.log('Restored Algolia queryID from session:', storedQueryID);
                        } else {
                            // Clear expired queryID
                            sessionStorage.removeItem('algolia_queryID');
                            sessionStorage.removeItem('algolia_queryID_timestamp');
                        }
                    }
                } catch (e) {
                    console.warn('Failed to restore queryID:', e);
                }

                if (settings) {
                    usedFacets = settings?.facets?.replaceAll("\n", "").split(",");
                    defaultSearchParams.attributesToRetrieve = settings?.attributes?.replaceAll("\n", "").split(",");
                    
                    // Debug: Log configured facets to verify size facets are included
                    console.log('Configured facets from settings:', usedFacets);
                    console.log('Size-related facets found:', usedFacets.filter(facet => 
                        facet.toLowerCase().includes('size') || facet.includes('options')
                    ));
                    console.log('Published/status facets found:', usedFacets.filter(facet => 
                        facet.toLowerCase().includes('published') || facet.toLowerCase().includes('status')
                    ));

                    this.productsPerPageSetting = productsPerPage;
                    this.headerResultsTotal = settings?.results_returned_header;

                    indexes = settings?.search_indexes?.split(',').reduce((acc, pair) => {
                        const [key, value] = pair.split(':').map(str => str.trim());
                        acc[key] = value;
                        return acc;
                    }, {});
                    
                    this.config = {
                        app: settings.app_id || null,
                        key: settings.api_key || null,
                        hide_out_of_stock: settings.filters_hide_out_of_stock,
                        index: indexes,
                    };

                    this.defaultSort = settings?.default_sort;
                    this.client = algoliasearch(this.config.app, this.config.key);
                    this.hasInit = true;

                    aa('init', {
                        appId: settings.app_id,
                        apiKey: settings.api_key,
                        useCookie: false,
                        enableInsights: true,
                    });

                    this.insightIndex = settings?.insight_index;
                    this.sortOptions = settings?.sort_indicies?.split(',').map(pair => {
                        const [key, value] = pair.split(':').map(str => str.trim());
                        return { key, value };
                    });

                    window.sortOptions = cloneDeep(this.sortOptions);
                    window.defaultSort = cloneDeep(this.defaultSort);

                    // Updated event listeners for unified search
                    window.addEventListener('searchrequest', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-filter-applied', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-sort-applied', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-nextpage', this.handlePaginationRequest.bind(this));
                    window.addEventListener('insight-add', this.handleAddToCartInsight.bind(this));
                    window.addEventListener('product-click', this.handleProductClickEvent.bind(this));
                    window.addEventListener('insight-purchase', (event) => this.handlePurchaseEvent(event.detail));
                    window.addEventListener('product-view', (event) => this.handleViewEvent(event.detail.productIds, event.detail.eventType));
                    
                    // Legacy event listeners for backward compatibility
                    window.addEventListener('searchrequest-articles', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-suggestions', this.handleUnifiedSearchRequest.bind(this));
                    
                    window.algoliaProductClick = this.handleProductClick.bind(this);
                    window.algoliaProductView = this.handleViewEvent.bind(this);
                    window.algoliaDebugTracking = this.debugTrackingState.bind(this);
                }
            },

            /**
             * Unified multi-index search request handler
             * Combines products, articles, categories, and suggestions into a single API call
             */
            async handleUnifiedSearchRequest(event) {
                const { query, type, filters, index, productsPerPage, isFiltering, searchOptions = {} } = event.detail;

                if (this.isProcessingRequest) {
                    this.cancelCurrentRequest();
                }

                this.isProcessingRequest = true;
                this.requestType = type;

                if (isFiltering !== undefined) {
                    this.isFiltering = isFiltering;
                }

                // Show spinner for appropriate request types
                if (this.requestType === 'search') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: { id: 'spinner-search' },
                    }));
                } else if (this.requestType === 'plp') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: { id: 'spinner-collection' },
                    }));
                }

                // Use the same search index logic for both header and main search
                const searchIndex = this.config.index[index] ? index : this.defaultSort;

                // Set hits per page based on request type
                if (this.requestType === 'header') {
                    defaultSearchParams.hitsPerPage = this.headerResultsTotal;
                } else {
                    defaultSearchParams.hitsPerPage = productsPerPage
                        ? productsPerPage
                        : this.productsPerPageSetting;
                }

                try {
                    // Build unified multi-index search queries
                    const queries = this.buildMultiIndexQueries(query, filters, searchIndex, productsPerPage, searchOptions);
                    
                    // Execute single multi-index search request
                    const results = await this.executeMultiIndexSearch(queries);
                    
                    // Process and dispatch results
                    await this.processMultiIndexResults(results, query, type);
                    
                } catch (error) {
                    console.error('Unified search request failed:', error);
                    this.dispatchErrorEvent(error);
                } finally {
                    this.isProcessingRequest = false;
                    window.dispatchEvent(new CustomEvent('loading', {
                        detail: { value: false },
                    }));
                }
            },

            /**
             * Build queries for multi-index search
             */
            buildMultiIndexQueries(query, filters, index, productsPerPage, searchOptions = {}) {
                const queries = [];
                const customSearchParams = this.buildCustomSearchParams(filters);

                // Determine which indexes to search based on request type and options
                const indexesToSearch = this.determineIndexesToSearch(index, searchOptions);

                indexesToSearch.forEach(({ indexKey, contentType }) => {
                    const indexName = this.config.index[indexKey];
                    if (!indexName) return;

                    // Get base search parameters for content type
                    const baseParams = cloneDeep(searchParamsByType[contentType] || searchParamsByType.products);
                    
                    // Apply custom parameters
                    // If no usedFacets configured or filters are being cleared, use all available facets
                    let facetsToUse = baseParams.facets;
                    if (usedFacets.length > 0) {
                        facetsToUse = usedFacets;
                    }
                    
                    // console.log('Algolia facets being requested:', facetsToUse);
                    // console.log('usedFacets:', usedFacets);
                    // console.log('baseParams.facets:', baseParams.facets);
                    
                    const searchParams = {
                        ...baseParams,
                        ...customSearchParams,
                        facets: usedFacets.length ? usedFacets : baseParams.facets,
                        userToken: this.getAnonymousUserToken(),
                        maxValuesPerFacet: 1000, // Ensure high limit for facet values
                    };
                    
                    // Debug: Log search parameters to verify maxValuesPerFacet is set
                    console.log('Algolia search params for', contentType, ':', {
                        indexName,
                        facets: searchParams.facets,
                        maxValuesPerFacet: searchParams.maxValuesPerFacet,
                        query: query || '',
                        filters: searchParams.filters,
                        fullSearchParams: searchParams
                    });

                    // Adjust hits per page based on content type and request
                    if (contentType === 'products') {
                        if (this.requestType === 'header') {
                            searchParams.hitsPerPage = this.headerResultsTotal || 5;
                        } else {
                            searchParams.hitsPerPage = productsPerPage || this.productsPerPageSetting || 20;
                        }
                    }

                    queries.push({
                        indexName,
                        query: query || '',
                        params: searchParams,
                        contentType,
                        indexKey,
                        originalParams: searchParams // Store original params for reference
                    });
                });

                return queries;
            },

            /**
             * Determine which indexes to search based on request type and options
             */
            determineIndexesToSearch(index, searchOptions = {}) {
                const indexes = [];

                // For sort operations, only search the specific sorted index for products
                if (index && this.config.index[index]) {
                    const contentType = this.getContentTypeForIndexKey(index);
                    indexes.push({ indexKey: index, contentType });
                    
                    // For multi-index, also include other content types with their default indexes
                    if (!searchOptions.contentTypes || searchOptions.contentTypes.length > 1) {
                        Object.keys(this.config.index).forEach(indexKey => {
                            const otherContentType = this.getContentTypeForIndexKey(indexKey);
                            // Only add if it's a different content type and not already added
                            if (otherContentType !== contentType && 
                                !indexes.some(idx => idx.contentType === otherContentType)) {
                                indexes.push({ indexKey, contentType: otherContentType });
                            }
                        });
                    }
                } else if (searchOptions.contentTypes) {
                    // Search only specified content types
                    searchOptions.contentTypes.forEach(contentType => {
                        const indexKey = this.getIndexKeyForContentType(contentType, index);
                        if (indexKey && this.config.index[indexKey]) {
                            indexes.push({ indexKey, contentType });
                        }
                    });
                } else {
                    // Default: search all available indexes
                    Object.keys(this.config.index).forEach(indexKey => {
                        const contentType = this.getContentTypeForIndexKey(indexKey);
                        indexes.push({ indexKey, contentType });
                    });
                }

                // console.log('Algolia - determined indexes to search:', indexes);
                return indexes;
            },

            /**
             * Get content type from index key
             */
            getContentTypeForIndexKey(indexKey) {
                if (indexKey.includes('article')) return 'articles';
                if (indexKey.includes('category') || indexKey.includes('collection')) return 'categories';
                if (indexKey.includes('suggestion') || indexKey.includes('query')) return 'suggestions';
                return 'products';
            },

            /**
             * Get index key for content type
             */
            getIndexKeyForContentType(contentType, preferredIndex) {
                if (preferredIndex && this.config.index[preferredIndex]) {
                    return preferredIndex;
                }

                // Map content types to likely index keys
                const typeToIndexMap = {
                    products: this.defaultSort || 'default',
                    articles: 'articles',
                    categories: 'categories',
                    suggestions: 'suggestions'
                };

                return typeToIndexMap[contentType] || this.defaultSort;
            },

            /**
             * Execute multi-index search with single API call
             */
            async executeMultiIndexSearch(queries) {
                try {
                    // console.log('Executing unified multi-index search:', queries.length, 'queries');
                    
                    // Use Algolia's multipleQueries for optimal performance
                    const searchQueries = queries.map(({ indexName, query, params }) => ({
                        indexName,
                        query,
                        params
                    }));

                    const algoliaResults = await this.client.multipleQueries(searchQueries);
                    
                    // Enhance results with metadata
                    algoliaResults.results = algoliaResults.results.map((result, index) => ({
                        ...result,
                        contentType: queries[index].contentType,
                        indexKey: queries[index].indexKey,
                        originalParams: queries[index].originalParams
                    }));

                    return algoliaResults;
                } catch (error) {
                    console.error('Multi-index search failed:', error);
                    throw error;
                }
            },

            /**
             * Process and dispatch multi-index search results
             */
            async processMultiIndexResults(algoliaResults, query, requestType) {
                // console.log('load more', algoliaResults);
                const resultsByType = {
                    products: [],
                    articles: [],
                    categories: [],
                    suggestions: []
                };

                let totalResults = 0;
                let filters = null;
                let pages = 1;
                let queryID = null;
                let allFilters = null;

                // Process each result set
                for (const result of algoliaResults.results) {
                    const { contentType } = result;
                    
                    // Capture queryID from any result (typically from products)
                    if (result.queryID && !queryID) {
                        queryID = result.queryID;
                        this.currentQueryID = queryID;
                        window.algoliaQueryID = queryID;
                        
                        // Persist queryID in sessionStorage for cross-page tracking
                        try {
                            sessionStorage.setItem('algolia_queryID', queryID);
                            sessionStorage.setItem('algolia_queryID_timestamp', Date.now().toString());
                        } catch (e) {
                            console.warn('Failed to persist queryID:', e);
                        }
                    }

                    if (contentType === 'products') {
                        // Transform products using existing method
                        const transformedProducts = await this.transformProducts(result);
                        resultsByType.products = transformedProducts.products;
                        totalResults = transformedProducts.total_results || result.nbHits;
                        filters = transformedProducts.filters;
                        pages = transformedProducts.pages || result.nbPages;
                        
                        // Get complete facets for better filter experience
                    console.log('About to call getCompleteFacets with:', { 
                        query, 
                        resultParams: result.params,
                        originalParams: result.originalParams,
                        resultFilters: result.params?.filters,
                        originalFilters: result.originalParams?.filters,
                        hasResults: totalResults > 0,
                        requestType: this.requestType,
                        mainSearchNbHits: result.nbHits
                    });                        // For search requests with 0 results, always try to get complete facets to help users
                        // For PLP requests, get complete facets as before
                        const shouldGetCompleteFacets = this.requestType === 'search' || 
                            (result.originalParams && result.originalParams.filters);
                        
                        if (shouldGetCompleteFacets) {
                            // Use original params that contain the filters
                            allFilters = await this.getCompleteFacets(query, result.originalParams);
                            console.log('getCompleteFacets returned:', allFilters);
                            
                            // For search requests with 0 results, use complete facets if main search has no filters
                            if (this.requestType === 'search' && totalResults === 0 && (!filters || filters.length === 0) && allFilters) {
                                console.log('Search with 0 results - using complete facets as fallback');
                                filters = allFilters;
                            }
                        }
                    } else {
                        // For other content types, use hits directly
                        resultsByType[contentType] = result.hits || [];
                        if (contentType === 'articles') {
                            totalResults += result.nbHits || 0;
                        }
                    }
                }

                // Use complete filters if available, otherwise use current filters
                // Use main search filters for UI (dynamically filtered)
                // Store complete filters for size component that needs all options
                const filtersToDispatch = filters; // Always use filtered results for UI
                
                // Store complete filters globally for FilterSize component to access
                if (allFilters) {
                    window.__completeFilters = allFilters;
                } else {
                    window.__completeFilters = null;
                }

                // Dispatch unified results event
                window.dispatchEvent(new CustomEvent('searchrequest-unified-complete', {
                    detail: {
                        query,
                        type: requestType,
                        queryID,
                        totalResults,
                        pages,
                        filters: filtersToDispatch,
                        isFiltering: this.isFiltering,
                        results: resultsByType
                    },
                }));

                // Dispatch backward-compatible events
                this.dispatchLegacyEvents(resultsByType, query, requestType, totalResults, filtersToDispatch, pages, queryID);

                // console.log('Unified multi-index search completed:', resultsByType);
            },

            /**
             * Dispatch legacy events for backward compatibility
             */
            dispatchLegacyEvents(resultsByType, query, requestType, totalResults, filters, pages, queryID) {
                // Always dispatch products event (even with empty results)
                window.dispatchEvent(new CustomEvent('searchrequest-products-complete', {
                    detail: {
                        query,
                        products: resultsByType.products,
                        totalResults: resultsByType.products.length > 0 ? totalResults : 0,
                        filters,
                        type: requestType,
                        page: 0, // Will be updated by pagination
                        isFiltering: this.isFiltering,
                        queryID
                    },
                }));

                // Always dispatch articles event (even with empty results)
                window.dispatchEvent(new CustomEvent('searchrequest-articles-complete', {
                    detail: {
                        articles: resultsByType.articles,
                        totalResults: resultsByType.articles.length,
                        type: requestType,
                    },
                }));

                // Always dispatch suggestions event - only real data from Algolia
                const suggestionData = { type: requestType };
                
                // Only use real suggestions from Algolia indexes
                suggestionData.suggestions = resultsByType.suggestions || [];
                suggestionData.categories = resultsByType.categories || [];

                window.dispatchEvent(new CustomEvent('searchrequest-suggestions-complete', {
                    detail: suggestionData,
                }));

                // Dispatch filter-list event for Collection component to update filters
                window.dispatchEvent(new CustomEvent('filter-list', {
                    detail: {
                        type: requestType,
                        filters: filters || []
                    },
                }));
            },

            /**
             * Get complete facets from Algolia dynamically for updated filter counts
             * Dynamically fetches updated facet data (including colour and size).
             * Ensures the facet list refreshes whenever new filters are applied.
             * Logs useful debug information for validation.
             */
            async getCompleteFacets(query, originalParams) {
                try {
                    const indexKey = Object.keys(this.config.index).find(key => key.includes('product')) || this.defaultSort;
                    const indexName = this.config.index[indexKey];
                    if (!indexName) {
                        console.warn('No valid product index found for getCompleteFacets');
                        return null;
                    }

                    const index = this.client.initIndex(indexName);

                    // Clone filters but remove pagination-related properties
                    const facetParams = {
                        ...originalParams,
                        facets: usedFacets.length ? usedFacets : ['vendor', 'product_type', 'tags', 'options.size', 'named_tags.swatch'],
                        maxValuesPerFacet: 1000,
                        hitsPerPage: 0,
                        page: 0
                    };

                    // console.log('Fetching updated facets with:', facetParams);

                    const facetResults = await index.search(query || '', facetParams);

                    if (facetResults && facetResults.facets) {
                        // console.log('Updated facets retrieved:', Object.keys(facetResults.facets));
                        return this.transformFacets(facetResults.facets);
                    }

                    console.warn('No facets returned from Algolia');
                    return null;
                } catch (error) {
                    console.error('Error getting complete facets:', error);
                    return null;
                }
            },

            /**
             * Dispatch error event
             */
            dispatchErrorEvent(error) {
                window.dispatchEvent(new CustomEvent('searchrequest-error', {
                    detail: {
                        error: error.message || 'Search request failed',
                        type: this.requestType
                    },
                }));
            },

            // Legacy methods maintained for backward compatibility
            async handleSearchRequest(event) {
                // Redirect to unified search
                await this.handleUnifiedSearchRequest(event);
            },

            async handleSuggestionSearchRequest(event) {
                // Redirect to unified search with suggestions focus
                const modifiedEvent = {
                    ...event,
                    detail: {
                        ...event.detail,
                        searchOptions: {
                            contentTypes: ['suggestions', 'categories']
                        }
                    }
                };
                await this.handleUnifiedSearchRequest(modifiedEvent);
            },

            async handleFilterRequest(event) {
                const { query, type, filters, index, isFilteringRequest } = event.detail;

                this.requestCountPromise = new Promise((resolve) => {
                    this.requestCountResolve = resolve;
                });

                if (this.isProcessingRequest) {
                    this.cancelCurrentRequest();
                }
                this.isProcessingRequest = true;
                this.requestType = type;

                this.isFiltering = isFilteringRequest;

                const searchIndex = this.config.index[index] ? index : this.defaultSort;

                if (this.requestType === 'search') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-search',
                        },
                    }));
                } else if (this.requestType === 'plp') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-collection',
                        },
                    }));
                }
                const customSearchParams = this.buildCustomSearchParams(filters);
                await this.algoliaProductSearch(query, customSearchParams, {}, searchIndex, true);
            },

            getAnonymousUserToken() {
                const tokenKey = 'uuid';
                let userToken = localStorage.getItem(tokenKey);
            
                if (!userToken || userToken === 'null' || userToken === 'undefined') {
                    userToken = crypto.randomUUID();
                    localStorage.setItem(tokenKey, userToken);
                }
                
                // Validate the token format (should be UUID)
                const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                if (!uuidRegex.test(userToken)) {
                    console.warn('Invalid userToken format, generating new one:', userToken);
                    userToken = crypto.randomUUID();
                    localStorage.setItem(tokenKey, userToken);
                }
            
                return userToken;
            },

            // Utility function for debugging Algolia tracking
            debugTrackingState() {
                const currentQueryID = this.currentQueryID || window.algoliaQueryID;
                const storedQueryID = sessionStorage.getItem('algolia_queryID');
                const storedTimestamp = sessionStorage.getItem('algolia_queryID_timestamp');
                
                return {
                    currentQueryID,
                    storedQueryID,
                    storedTimestamp,
                    timestampAge: storedTimestamp ? Date.now() - parseInt(storedTimestamp) : null,
                    isQueryIDValid: storedTimestamp ? (Date.now() - parseInt(storedTimestamp)) < (30 * 60 * 1000) : false,
                    userToken: this.getAnonymousUserToken(),
                    insightIndex: this.insightIndex,
                    customerId: window.customerId || window.customer?.id
                };
            },

            handleViewEvent(productIds, eventType = 'Product Viewed') {
                if (!Array.isArray(productIds)) {
                    productIds = [productIds];
                }

                // Ensure we're using objectIDs (not Shopify product IDs)
                const objectIDs = productIds.map(id => id.toString());
                
                const eventData = {
                    userToken: this.getAnonymousUserToken(),
                    index: this.insightIndex,
                    eventName: eventType,
                    objectIDs
                };

                // Add authenticated user token if customer is logged in
                const customerId = window.customerId || window.customer?.id;
                if (customerId && customerId !== '' && customerId !== 'null') {
                    eventData.authenticatedUserToken = customerId.toString();
                }

                // Fire view event
                aa('viewedObjectIDs', eventData);
                
                // console.log('Product view tracked:', {
                //     objectIDs,
                //     eventType,
                //     customerId,
                //     hasCustomerId: !!(customerId && customerId !== '' && customerId !== 'null')
                // });
            },

            async handleAddToCartInsight(event) {
                try {
                    const startTime = Date.now();
                    
                    const response = await fetch(`/cart.js`);
                    if (!response.ok) {
                        throw new Error(`Cart fetch failed: ${response.status} ${response.statusText}`);
                    }
                    const cartData = await response.json();

                    // Validate cart data
                    if (!cartData.items || cartData.items.length === 0) {
                        console.warn(`[${startTime}] No items in cart, skipping insight tracking`);
                        return;
                    }

                    // Get customer ID with validation
                    const customerId = event.detail?.customer_id || window.customerId || window.customer?.id;
                    const hasValidCustomerId = customerId && customerId !== '' && customerId !== 'null' && customerId !== 'undefined';

                    // Group by product to handle multiple variants of same product
                    const productMap = new Map();
                    
                    // Since Algolia objectIDs are variant IDs, we can use them directly from cart items

                    cartData.items.forEach((item, index) => {
                        try {
                            // Debug each cart item
                            console.log(`Processing cart item ${index}:`, {
                                productId: item.product_id,
                                variantId: item.variant_id,
                                title: item.title,
                                quantity: item.quantity,
                                originalPrice: item.original_price,
                                finalLinePrice: item.final_line_price,
                                originalLinePrice: item.original_line_price
                            });

                            // Validate required item fields
                            if (!item.product_id) {
                                console.warn(`[${startTime}] Item ${index} missing product_id:`, item);
                                return;
                            }

                            const shopifyProductId = item.product_id.toString();
                            const variantId = item.variant_id ? item.variant_id.toString() : null;
                            
                            // Use variant ID as Algolia objectID (since your Algolia index uses variant IDs as objectIDs)
                            const trackingId = variantId || shopifyProductId; // Fallback to product ID if no variant ID
                            
                            // console.log(`Using Algolia objectID ${trackingId} (variant: ${variantId}, product: ${shopifyProductId})`);
                            
                            // console.log(`Item ${index} objectID mapping:`, { 
                            //     shopifyProductId, 
                            //     variantId, 
                            //     trackingId,
                            //     title: item.title,
                            //     usingVariantLevel: !!variantId 
                            // });
                            
                            const originalPrice = typeof item.original_price === 'number' ? item.original_price : 0;
                            const finalLinePrice = typeof item.final_line_price === 'number' ? item.final_line_price : originalPrice;
                            const originalLinePrice = typeof item.original_line_price === 'number' ? item.original_line_price : originalPrice;
                            
                            if (productMap.has(trackingId)) {
                                const existing = productMap.get(trackingId);
                                existing.quantity += item.quantity || 0;
                                existing.price = Math.max(existing.price, originalPrice / 100);
                                existing.originalLinePrice += originalLinePrice;
                                existing.finalLinePrice += finalLinePrice;
                                
                                // Add variant details for grouped tracking
                                if (variantId && !existing.variants.includes(variantId)) {
                                    existing.variants.push(variantId);
                                    existing.variantDetails.push({
                                        variantId: variantId,
                                        quantity: item.quantity || 0,
                                        price: originalPrice / 100,
                                        title: item.title
                                    });
                                }
                                
                                // console.log(`Updated existing product ${trackingId} with ${existing.variants.length} variants:`, existing);
                            } else {
                                const newProduct = {
                                    objectID: trackingId, // Use variant ID for Algolia compatibility
                                    productId: shopifyProductId, // Keep product ID for reference
                                    variants: variantId ? [variantId] : [], // Track which variants are included
                                    price: originalPrice / 100,
                                    quantity: item.quantity || 0,
                                    originalLinePrice: originalLinePrice,
                                    finalLinePrice: finalLinePrice,
                                    title: item.title, // Add title for better debugging
                                    variantDetails: variantId ? [{
                                        variantId: variantId,
                                        quantity: item.quantity || 0,
                                        price: originalPrice / 100,
                                        title: item.title
                                    }] : []
                                };
                                productMap.set(trackingId, newProduct);
                                // console.log(`Added new variant ${trackingId}:`, newProduct);
                            }
                        } catch (itemError) {
                            console.error(`[${startTime}] Error processing item ${index}:`, itemError, item);
                        }
                    });

                    if (productMap.size === 0) {
                        console.warn(`[${startTime}] No valid products found in cart`);
                        return;
                    }

                    console.log(`Final productMap:`, {
                        size: productMap.size,
                        entries: Array.from(productMap.entries())
                    });

                    // Build objectIDs and objectData with enhanced validation
                    // Ensure objectIDs are strings for Algolia (they expect product IDs as objectIDs)
                    const objectIDs = Array.from(productMap.keys()).map(id => id.toString());
                    
                    console.log(`Final objectIDs for Algolia:`, objectIDs);
                    const objectData = Array.from(productMap.values()).map((item, index) => {
                        try {
                            const price = Number(item.price);
                            const discount = item.originalLinePrice > item.finalLinePrice 
                                ? Number(((item.originalLinePrice - item.finalLinePrice) / 100).toFixed(2))
                                : 0;
                            
                            // Validate price is a valid number
                            if (!Number.isFinite(price) || price < 0) {
                                console.warn(`[${startTime}] Invalid price for item ${index}:`, price, item);
                                return { price: 0, discount: 0, quantity: item.quantity };
                            }
                            
                            return {
                                price: Number(price.toFixed(2)),
                                discount: Number(discount.toFixed(2)),
                                quantity: item.quantity
                            };
                        } catch (dataError) {
                            console.error(`[${startTime}] Error building object data for item ${index}:`, dataError, item);
                            return { price: 0, discount: 0, quantity: item.quantity || 0 };
                        }
                    });

                    // Calculate total value with validation
                    const calculatedValue = objectData.reduce((sum, item) => {
                        const itemValue = (item.price || 0) * (item.quantity || 0);
                        return sum + itemValue;
                    }, 0);
                    const value = Number(calculatedValue.toFixed(2));
                    
                    // Validate currency format
                    const rawCurrency = cartData?.currency || 'AUD';
                    const currency = typeof rawCurrency === 'string' ? rawCurrency.toUpperCase() : 'AUD';
                    
                    // Validate currency is 3-letter ISO code
                    if (!/^[A-Z]{3}$/.test(currency)) {
                        console.warn(`[${startTime}] Invalid currency format:`, rawCurrency, 'defaulting to AUD');
                        currency = 'AUD';
                    }

                    // Get and validate user token
                    const userToken = this.getAnonymousUserToken();
                    if (!userToken) {
                        console.error(`[${startTime}] Failed to get user token`);
                        return;
                    }

                    // Validate insight index
                    if (!this.insightIndex) {
                        console.error(`[${startTime}] Missing insight index configuration`);
                        return;
                    }

                    const baseEventData = {
                        eventType: 'conversion',
                        userToken: userToken,
                        index: this.insightIndex,
                        objectIDs: objectIDs,
                        objectData: objectData,
                        value: value,
                        currency: currency
                    };

                    // Add authenticated user token with validation
                    if (hasValidCustomerId) {
                        baseEventData.authenticatedUserToken = customerId.toString();
                    }

                    // Track single canonical add to cart event
                    // Check for queryID from multiple sources for better attribution
                    const cartQueryID = this.currentQueryID || window.algoliaQueryID || sessionStorage.getItem('algolia_queryID');
                    
                    try {
                        if (cartQueryID) {
                            // Verify queryID is still valid (within 30 minutes)
                            const timestamp = sessionStorage.getItem('algolia_queryID_timestamp');
                            const thirtyMinutes = 30 * 60 * 1000;
                            const isValidTimestamp = timestamp && (Date.now() - parseInt(timestamp)) < thirtyMinutes;
                            
                            if (isValidTimestamp || this.currentQueryID) {
                                // Add-to-cart after search - includes queryID for attribution
                                const addToCartPayload = {
                                    ...baseEventData,
                                    eventName: 'Product Added to Cart After Search',
                                    queryID: cartQueryID
                                };
                                
                                aa('addedToCartObjectIDsAfterSearch', addToCartPayload);
                                
                                console.log('Add to cart tracked with search attribution:', {
                                    queryID: cartQueryID,
                                    objectIDs,
                                    objectIDsCount: objectIDs.length,
                                    cartItemsCount: cartData.items.length,
                                    productMapSize: productMap.size,
                                    value,
                                    objectData: objectData.map(item => ({ price: item.price, quantity: item.quantity }))
                                });
                            } else {
                                // QueryID is too old, track as direct add to cart
                                const addToCartPayload = {
                                    ...baseEventData,
                                    eventName: 'Product Added to Cart'
                                };
                                
                                aa('addedToCartObjectIDs', addToCartPayload);
                                
                                // console.log('Add to cart tracked without search attribution (queryID expired):', {
                                //     objectIDs,
                                //     value
                                // });
                            }
                        } else {
                            const addToCartPayload = {
                                ...baseEventData,
                                eventName: 'Product Added to Cart'
                            };
                            
                            aa('addedToCartObjectIDs', addToCartPayload);
                            
                            // console.log('Add to cart tracked without search attribution:', {
                            //     objectIDs,
                            //     value
                            // });
                        }
                    } catch (addError) {
                        console.error(`[${startTime}] Add to cart event failed:`, addError);
                    }

                    const endTime = Date.now();

                } catch (error) {
                    console.error("Critical error in add to cart tracking:", error);
                    console.error("Error details:", {
                        message: error.message,
                        stack: error.stack,
                        name: error.name,
                        cause: error.cause
                    });
                    
                    // Try to log the current state for debugging
                    try {
                        console.error("Debug state:", {
                            hasInsightIndex: !!this.insightIndex,
                            hasCurrentQueryID: !!this.currentQueryID,
                            userToken: this.getAnonymousUserToken(),
                            customerId: window.customerId,
                            eventDetail: event?.detail
                        });
                    } catch (debugError) {
                        console.error("Failed to log debug state:", debugError);
                    }
                }
            },

            // Add method for validating objectID format
            validateObjectID(objectID, productContext = {}) {
                if (!objectID) return { valid: false, reason: 'Missing objectID' };
                
                // Check if this looks like a Shopify ID (numeric, usually 13+ digits)
                const isShopifyFormat = /^\d{10,}$/.test(objectID.toString());
                
                return {
                    valid: true,
                    objectID: objectID.toString(),
                    isShopifyFormat,
                    hasAlgoliaObjectID: !!productContext.objectID,
                    recommendation: isShopifyFormat && !productContext.objectID 
                        ? 'Consider verifying this Shopify ID matches your Algolia index objectID format' 
                        : 'ObjectID appears to be in correct format'
                };
            },

            // Method to validate and test conversion events
            async testConversionFlow() {
                console.group('Testing Algolia Conversion Flow');
                
                // Check current state
                const state = this.debugTrackingState();
                
                // Test cart fetch
                try {
                    const response = await fetch('/cart.js');
                    const cart = await response.json();
                    
                    if (cart.items.length === 0) {
                        console.warn('Cart is empty - add items to test conversion tracking');
                    } else {
                        // Simulate conversion event
                        console.log('Would track conversion with these objectIDs:', 
                            cart.items.map(item => item.product_id.toString()));
                    }
                } catch (error) {
                    console.error('Failed to fetch cart:', error);
                }
                
                console.groupEnd();
                return state;
            },

            // Method to check recent Algolia events
            getRecentAlgoliaEvents() {
                // This would need to be implemented with actual event storage
                // For now, just return debugging info
                return {
                    message: 'Check browser network tab for calls to algolia-analytics.algolia.net',
                    queryID: this.currentQueryID,
                    userToken: this.getAnonymousUserToken(),
                    insightIndex: this.insightIndex
                };
            },

            handleProductClick(productId, productTitle, productUrl, queryID) {
                const clickQueryID = queryID || this.currentQueryID || window.algoliaQueryID;
                
                // Ensure we have a valid productId (objectID)
                if (!productId) {
                    console.warn('Product click not tracked - missing productId/objectID');
                    return;
                }

                const objectID = productId.toString();
                
                if (clickQueryID) {
                    // Track as search-attributed click
                    const eventData = {
                        userToken: this.getAnonymousUserToken(),
                        index: this.insightIndex,
                        eventName: 'Product Clicked After Search',
                        queryID: clickQueryID,
                        objectIDs: [objectID],
                        positions: [1] // Default position, could be enhanced with actual position
                    };

                    // Add authenticated user token if customer is logged in
                    const customerId = window.customerId || window.customer?.id;
                    if (customerId && customerId !== '' && customerId !== 'null') {
                        eventData.authenticatedUserToken = customerId.toString();
                    }

                    aa('clickedObjectIDsAfterSearch', eventData);
                    
                    // console.log('Product click tracked (with search attribution):', {
                    //     objectID: objectID,
                    //     productTitle,
                    //     productUrl,
                    //     queryID: clickQueryID,
                    //     hasCustomerId: !!customerId
                    // });
                } else {
                    // Track as direct click (no search attribution)
                    const eventData = {
                        userToken: this.getAnonymousUserToken(),
                        index: this.insightIndex,
                        eventName: 'Product Clicked',
                        objectIDs: [objectID]
                    };

                    // Add authenticated user token if customer is logged in
                    const customerId = window.customerId || window.customer?.id;
                    if (customerId && customerId !== '' && customerId !== 'null') {
                        eventData.authenticatedUserToken = customerId.toString();
                    }

                    aa('clickedObjectIDs', eventData);
                    
                    // console.log('Product click tracked (no search attribution):', {
                    //     objectID: objectID,
                    //     productTitle,
                    //     productUrl,
                    //     hasCustomerId: !!customerId
                    // });
                }
            },

            handleProductClickEvent(event) {
                const { productId, productTitle, productUrl, queryID } = event.detail;
                this.handleProductClick(productId, productTitle, productUrl, queryID);
            },

            handlePurchaseEvent(orderData) {
                try {
                    if (!orderData || !orderData.line_items) {
                        console.warn('Purchase event not tracked - missing order data');
                        return;
                    }

                    // Map line items to product IDs and prepare object data
                    const productMap = new Map();
                    
                    orderData.line_items.forEach(item => {
                        const productId = item.product_id.toString();
                        if (productMap.has(productId)) {
                            const existing = productMap.get(productId);
                            existing.quantity += item.quantity;
                            existing.revenue += (item.price * item.quantity);
                        } else {
                            productMap.set(productId, {
                                objectID: productId,
                                price: item.price,
                                quantity: item.quantity,
                                revenue: item.price * item.quantity
                            });
                        }
                    });

                    // Ensure objectIDs are strings (Algolia expects product IDs as objectIDs)
                    const objectIDs = Array.from(productMap.keys()).map(id => id.toString());
                    const objectData = Array.from(productMap.values()).map(item => ({
                        price: item.price,
                        quantity: item.quantity
                    }));

                    const value = orderData.total_price || Array.from(productMap.values())
                        .reduce((sum, item) => sum + item.revenue, 0);

                    // Get customer ID from order or window
                    const customerId = orderData.customer?.id || window.customerId || window.customer?.id;
                    
                    const baseEventData = {
                        userToken: this.getAnonymousUserToken(),
                        index: this.insightIndex,
                        objectIDs,
                        objectData,
                        value,
                        currency: orderData.currency || 'AUD'
                    };

                    // Add authenticated user token if customer is logged in
                    if (customerId && customerId !== '' && customerId !== 'null') {
                        baseEventData.authenticatedUserToken = customerId.toString();
                    }

                    // Track purchase/conversion event
                    // Check for queryID from multiple sources for better attribution
                    const conversionQueryID = this.currentQueryID || window.algoliaQueryID || sessionStorage.getItem('algolia_queryID');
                    
                    if (conversionQueryID) {
                        // Verify queryID is still valid (within 30 minutes)
                        const timestamp = sessionStorage.getItem('algolia_queryID_timestamp');
                        const thirtyMinutes = 30 * 60 * 1000;
                        const isValidTimestamp = timestamp && (Date.now() - parseInt(timestamp)) < thirtyMinutes;
                        
                        if (isValidTimestamp || this.currentQueryID) {
                            aa('convertedObjectIDsAfterSearch', {
                                ...baseEventData,
                                eventName: 'Purchase After Search',
                                queryID: conversionQueryID
                            });
                            
                            console.log('Purchase tracked with search attribution:', {
                                queryID: conversionQueryID,
                                objectIDs,
                                value
                            });
                        } else {
                            // QueryID is too old, track as direct conversion
                            aa('convertedObjectIDs', {
                                ...baseEventData,
                                eventName: 'Purchase'
                            });
                            
                            console.log('Purchase tracked without search attribution (queryID expired):', {
                                objectIDs,
                                value
                            });
                        }
                    } else {
                        aa('convertedObjectIDs', {
                            ...baseEventData,
                            eventName: 'Purchase'
                        });
                        
                        console.log('Purchase tracked without search attribution:', {
                            objectIDs,
                            value
                        });
                    }

                    console.log('Purchase event tracked:', {
                        orderNumber: orderData.order_number || orderData.name,
                        objectIDs,
                        value,
                        hasQueryID: !!this.currentQueryID,
                        hasCustomerId: !!customerId
                    });

                } catch (error) {
                    console.error("Error tracking purchase:", error);
                }
            },

            buildCustomSearchParams(filters, page) {
                let customSearchParams = {};
                if (filters) {
                    if (filters.collection) {
                        customSearchParams.filters = `collections:${filters.collection}`;
                    } else {
                        if (this.config.hide_out_of_stock) {
                            customSearchParams.filters = filters + ` AND inventory_available:true`;
                        } else {
                            customSearchParams.filters = filters;
                        }
                    }
                }
                if (page) {
                    customSearchParams.page = page;
                }
                return customSearchParams;
            },

            cancelCurrentRequest() {
                if (this.currentRequest && typeof this.currentRequest.cancel === 'function') {
                    this.currentRequest.cancel();
                }
                this.isProcessingRequest = false;
            },

            async transformProducts(algoliaResults) {
                const productIdsList = [...new Set(algoliaResults.hits.map(product => product.id))];
                // console.log('Transform Products Debug:', {
                //     algoliaHitsCount: algoliaResults.hits.length,
                //     uniqueProductIds: productIdsList.length,
                //     algoliaReportedTotal: algoliaResults.nbHits,
                //     sampleProductIds: productIdsList.slice(0, 5) // First 5 IDs for debugging
                // });
                
                const transformedProducts = await shopify.getShopifyProductList(productIdsList);
                
                // Create a map of Algolia objectIDs to preserve them in the final product data
                const algoliaObjectIDMap = new Map();
                algoliaResults.hits.forEach(hit => {
                    if (hit.objectID && hit.id) {
                        algoliaObjectIDMap.set(hit.id.toString(), hit.objectID);
                    }
                });
                
                // Enhance transformed products with Algolia objectIDs
                const productsWithObjectIDs = transformedProducts.map(product => {
                    const algoliaObjectID = algoliaObjectIDMap.get(product.id.toString());
                    return {
                        ...product,
                        // Preserve Algolia objectID for proper tracking
                        objectID: algoliaObjectID || product.id.toString(),
                        // Add flag to indicate this came from Algolia search
                        _algoliaSource: !!algoliaObjectID
                    };
                });
                
                const output = {
                    products: productsWithObjectIDs,
                    page: algoliaResults.page,
                    filters: null,
                    total_results: null,
                    pages: null,
                };

                if (algoliaResults.facets) {
                    output.filters = this.transformFacets(algoliaResults.facets);
                }

                if (algoliaResults.nbHits > -1) {
                    output.total_results = algoliaResults.nbHits;
                }

                if (algoliaResults.nbPages > -1) {
                    output.pages = algoliaResults.nbPages;
                }

                return output;
            },

            transformFacets(facets) {
                let acc = []
                
                // Debug: Log raw facets data to see what Algolia is returning
                // console.log('Raw Algolia facets response:', facets);
                
                // Specifically check for size-related facets
                Object.entries(facets).forEach(([key, values]) => {
                    if (key.toLowerCase().includes('size') || key.includes('options.size')) {
                        console.log(`Size facet "${key}" has ${Object.keys(values).length} values:`, values);
                    }
                    // Also check for published/status facets to verify they exist
                    if (key.toLowerCase().includes('published') || key.toLowerCase().includes('status')) {
                        console.log(`Published/Status facet "${key}" has values:`, values);
                    }
                });

                return Object.entries(facets).reduce((accumulator, [facetKey, facetValues]) => {
                    let facetLabel = facetKey.split('.').pop();
                    if (facetLabel === 'inventory_available') {
                        facetLabel = 'availability';
                    } else if (facetLabel === 'price_range') {
                        facetLabel = 'price';
                    }

                    const values = Object.entries(facetValues).reduce((acc, [valueKey, valueCount]) => {
                        let valueLabel = valueKey;
                        if (valueLabel === 'true') {
                            valueLabel = 'available';
                        } else if (valueLabel === 'false') {
                            valueLabel = 'unavailable';
                        }
                        acc.push({ key: valueKey, label: valueLabel, count: valueCount });
                        return acc;
                    }, []).sort((a, b) => {
                        if (facetLabel === 'price') {
                            return parseFloat(a.label.split(':')[0]) - parseFloat(b.label.split(':')[0]);
                        }
                        return a.label.localeCompare(b.label);
                    });

                    acc.push({ label: facetLabel, key: facetKey, values });
                    return acc;
                }, []).sort((a, b) => sortedFacets.indexOf(a.key) - sortedFacets.indexOf(b.key));
            },

            // Legacy individual search methods maintained for backward compatibility
            async algoliaProductSearch(searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = {
                    ...defaultSearchParams,
                    facets: usedFacets,
                    userToken: this.getAnonymousUserToken(),
                    maxValuesPerFacet: 1000, // Ensure high limit for facet values
                    ...customSearchParams,
                };

                try {
                    const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);
                    
                    // Debug: Log the raw Algolia response to check facet data and sample product attributes
                    console.log('Raw Algolia search response summary:', {
                        nbHits: algoliaResults.nbHits,
                        hitsLength: algoliaResults.hits?.length || 0,
                        facetsCount: Object.keys(algoliaResults.facets || {}).length,
                        maxValuesPerFacetUsed: searchParams.maxValuesPerFacet
                    });
                    
                    // Log sample product attributes separately for better visibility
                    if (algoliaResults.hits && algoliaResults.hits.length > 0) {
                        console.log('Sample product (first hit):', algoliaResults.hits[0]);
                        console.log('Sample product keys:', Object.keys(algoliaResults.hits[0]));
                        
                        // Check for any status/visibility related fields
                        const sampleProduct = algoliaResults.hits[0];
                        const statusFields = Object.keys(sampleProduct).filter(key => 
                            key.toLowerCase().includes('status') || 
                            key.toLowerCase().includes('published') || 
                            key.toLowerCase().includes('visible') ||
                            key.toLowerCase().includes('active') ||
                            key.toLowerCase().includes('available')
                        );
                        console.log('Potential status/visibility fields in product:', statusFields);
                        statusFields.forEach(field => {
                            console.log(`${field}:`, sampleProduct[field]);
                        });
                    } else {
                        console.log('No hits returned from Algolia');
                    }
                    
                    if (algoliaResults.queryID) {
                        this.currentQueryID = algoliaResults.queryID;
                        window.algoliaQueryID = algoliaResults.queryID;
                        
                        // Persist queryID in sessionStorage for cross-page tracking
                        try {
                            sessionStorage.setItem('algolia_query_id', algoliaResults.queryID);
                            sessionStorage.setItem('algolia_queryID_timestamp', Date.now().toString());
                        } catch (e) {
                            console.warn('Failed to persist queryID:', e);
                        }
                    }
                    
                    const results = await this.transformProducts(algoliaResults);

                    // Always use current filters for dispatch to ensure all available options are shown
                    let filtersToDispatch = results.filters;

                    window.dispatchEvent(new CustomEvent('searchrequest-products-complete', {
                        detail: {
                            query: searchQuery,
                            products: results.products,
                            totalResults: results.total_results,
                            filters: filtersToDispatch,
                            type: this.requestType,
                            page: results.page,
                            isFiltering: this.isFiltering,
                            queryID: this.currentQueryID
                        },
                    }));
                    // console.log('Algolia search finished', results);
                    this.isProcessingRequest = false;
                    return results;
                } catch (err) {
                    console.log('ERROR: ', err);
                    this.isProcessingRequest = false;
                    return null;
                }
            },

            async algoliaArticleSearch(searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = {
                    ...defaultSearchParams,
                    facets: usedFacets,
                    userToken: this.getAnonymousUserToken(),
                    maxValuesPerFacet: 1000, // Ensure high limit for facet values
                    ...customSearchParams,
                };
                try {
                    const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);

                    window.dispatchEvent(new CustomEvent('searchrequest-articles-complete', {
                        detail: {
                            articles: algoliaResults.hits,
                            totalResults: algoliaResults.nbHits,
                            type: this.requestType,
                        },
                    }));
                    this.isProcessingRequest = false;
                    return algoliaResults;
                } catch (err) {
                    console.log('ERROR: ', err);
                    this.isProcessingRequest = false;
                    return null;
                }
            },

            async algoliaSuggestionsSearch(queries) {
                try {
                    const eventDetails = {
                        type: this.requestType,
                    };
                    const algoliaResults = await this.client.multipleQueries(queries);
                    Object.keys(this.config.index).forEach((key) => {
                        const indexName = this.config.index[key];
                        const matchingResults = algoliaResults.results.find((result) => result.index.includes(indexName))?.hits || [];
                        if (matchingResults.length > 0) {
                            eventDetails[key] = matchingResults;
                        }
                    });
                    window.dispatchEvent(new CustomEvent('searchrequest-suggestions-complete', {
                        detail: { ...eventDetails },
                    }));
                    this.isProcessingRequest = false;
                    return algoliaResults;
                } catch (err) {
                    console.log('ERROR: ', err);
                    this.isProcessingRequest = false;
                    return null;
                }
            },

            async algoliaGenericSearch(searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = {
                    ...defaultSearchParams,
                    facets: ['vendor', 'meta.information.range'],
                    userToken: this.getAnonymousUserToken(),
                    maxValuesPerFacet: 1000, // Ensure high limit for facet values
                    ...customSearchParams,
                };

                try {
                    const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);
                    return algoliaResults;
                } catch (err) {
                    console.log('ERROR: ', err);
                    return null;
                }
            },

            async algoliaGetProduct(id, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = { 
                    ...defaultSearchParams,
                    userToken: this.getAnonymousUserToken()
                };

                try {
                    const result = await index.search(`${id}`, searchParams, {});
                    if (!result.hits) return null;
                    return result.hits[0].objectID;
                } catch (err) {
                    console.log('ERROR: ', err);
                    return null;
                }
            },

            async algoliaGetRelatedProducts(objectID, customSearchParams = {}, indexKey, count) {
                const indexName = config.index[indexKey];
                const searchParams = { ...customSearchParams };

                try {
                    const relatedProducts = await recommendClient.getRelatedProducts([
                        {
                            indexName,
                            objectID,
                            maxRecommendations: count,
                            queryParameters: searchParams,
                        },
                    ]);
                    if (!relatedProducts.length || !relatedProducts[0].hits) return null;
                    return await this.transformProducts(relatedProducts[0]);
                } catch (err) {
                    console.log('ERROR: ', err);
                    return null;
                }
            },

            async handlePaginationRequest(event) {
                const { query, type, filters, page, index, productsPerPage } = event.detail;
                // console.log('Search', 'Request for search', productsPerPage);

                if (this.isProcessingRequest) {
                    this.cancelCurrentRequest();
                }
                this.isProcessingRequest = true;
                this.requestType = type;

                if (this.requestType === 'search') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-search',
                        },
                    }));
                } else if (this.requestType === 'plp') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-collection',
                        },
                    }));
                }

                if (this.requestType === 'header') {
                    defaultSearchParams.hitsPerPage = this.headerResultsTotal;
                } else {
                    defaultSearchParams.hitsPerPage = productsPerPage
                        ? productsPerPage
                        : this.productsPerPageSetting;
                }

                const searchIndex = this.config.index[index] ? index : this.defaultSort;


                const customSearchParams = this.buildCustomSearchParams(filters, page);
                const results = await this.algoliaProductSearch(query, customSearchParams, {}, searchIndex);
            }
        };
    },
};

export default algolia;